/**
 * Grupo "Conversations" de una ficha de permisos: los canales del store con el nivel del **sujeto**
 * de esa ficha (N1 para un operador, N2 para un seller).
 *
 * Es la **transpuesta** de la grilla del canal (`pages/channels/ChannelPermissions`): en vez de
 * "un canal → sus operadores/sellers", muestra "un sujeto → los canales del store". Misma tabla y
 * mismos niveles; lo único que cambia es por dónde se entra.
 *
 * Las dos variantes escriben en tablas distintas —`channel_operator_permissions` y
 * `channel_seller_permissions`— y para un seller el nivel es **de la entidad**: sus operadores lo
 * heredan read-only (001, rev 4), no se duplica por operador.
 *
 * **Habla con el conversation-service, no con el main API.** Es el único grupo de esa pantalla que
 * lo hace, porque los permisos de canal viven en `conversations.channel_operator_permissions`
 * (base `xcons_services`) y no en el modelo `permissions`/`configs` que alimenta al resto.
 * Espejarlos al main API sería duplicar estado.
 *
 * Vive acá y no dentro de la pantalla de store porque hay **tres** copias de `UserPermissionsPanel`
 * (store, linked_seller, linked_store): escribirlo en cada una obligaría a repetir cada cambio tres
 * veces.
 *
 * El padre dispara `persist()` desde su botón Save, igual que `ChannelPermissions`; sólo se manda lo
 * que cambió.
 */
import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Button, Card, Flex, Modal, Select, Typography, message } from "antd";
import {
  ArrowLeftOutlined,
  ExclamationCircleOutlined,
  MessageOutlined,
} from "@ant-design/icons";
import {
  ChannelPermissionLevel,
  conversationsApi,
  isConversationsConfigured,
} from "@/services/conversations.api";
import { NIVEL, NIVEL_PARA_TODO } from "@/utilities/niveles-de-permiso";

const { Text } = Typography;

/** Mismos labels que la grilla del canal: el "para todo" es porque el nivel es channel-wide. */
const LEVEL_OPTIONS = [
  { value: "no_access", label: NIVEL.no_access },
  { value: "viewer", label: NIVEL_PARA_TODO.viewer },
  { value: "editor", label: NIVEL_PARA_TODO.editor },
];

interface Grant {
  channelId: string;
  name: string;
  identifier: string;
  level: ChannelPermissionLevel;
}

export interface ChannelAccessPermissionsRef {
  /** Persiste los niveles cambiados. Lo llama el Save de la ficha. */
  persist: () => Promise<void>;
}

/** De quién son los permisos que muestra la ficha. */
export type ChannelAccessSubject =
  | { kind: "operator"; userId: number }
  | { kind: "seller"; sellerCode: string };

export const ChannelAccessPermissions = forwardRef<
  ChannelAccessPermissionsRef,
  { storeCode: string; subject: ChannelAccessSubject; editable?: boolean }
>(({ storeCode, subject, editable = true }, ref) => {
  const [grants, setGrants] = useState<Grant[]>([]);
  const [levels, setLevels] = useState<Record<string, ChannelPermissionLevel>>({});
  const [initial, setInitial] = useState<Record<string, ChannelPermissionLevel>>({});
  const [loading, setLoading] = useState(false);

  // Clave estable del sujeto, para no re-pedir en cada render por identidad de objeto.
  const subjectKey = subject.kind === "operator" ? `op:${subject.userId}` : `se:${subject.sellerCode}`;
  const hasSubject = subject.kind === "operator" ? Boolean(subject.userId) : Boolean(subject.sellerCode);

  useEffect(() => {
    // Sin URL del conv-service no hay a dónde pedir los canales: se oculta el grupo en vez de
    // mostrarlo vacío, que se leería como "no tiene acceso a ninguno".
    if (!isConversationsConfigured() || !storeCode || !hasSubject) return;
    let alive = true;
    setLoading(true);
    (subject.kind === "operator"
      ? conversationsApi.listOperatorChannelGrants(storeCode, subject.userId)
      : conversationsApi.listSellerChannelGrants(storeCode, subject.sellerCode))
      .then((rows) => {
        if (!alive) return;
        setGrants(rows);
        const map = Object.fromEntries(rows.map((r) => [r.channelId, r.level]));
        setLevels(map);
        setInitial(map);
      })
      .catch(() => alive && setGrants([]))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeCode, subjectKey]);

  useImperativeHandle(ref, () => ({
    persist: async () => {
      const entries = Object.entries(levels)
        .filter(([channelId, level]) => initial[channelId] !== level)
        .map(([channelId, level]) => ({ channelId, level }));
      if (!entries.length) return;
      if (subject.kind === "operator") {
        await conversationsApi.setOperatorChannelGrants(storeCode, subject.userId, entries);
      } else {
        await conversationsApi.setSellerChannelGrants(storeCode, subject.sellerCode, entries);
      }
      setInitial({ ...levels });
    },
  }));

  const confirmRemoveParticipations = () => {
    Modal.confirm({
      title: "¿Quitar todas las participaciones en conversaciones?",
      content:
        "El operador dejará de figurar como participante en las conversaciones donde está hoy. " +
        "No cambia su acceso a los canales —eso se configura con los selectores— ni quién atendió " +
        "cada conversación. Esta acción no se puede deshacer.",
      width: 520,
      okText: "Quitar participaciones",
      okType: "danger",
      okButtonProps: { type: "primary" },
      icon: <ExclamationCircleOutlined />,
      cancelButtonProps: { icon: <ArrowLeftOutlined /> },
      cancelText: "Atrás",
      onOk: async () => {
        try {
          if (subject.kind !== "operator") return;
          const { deleted } = await conversationsApi.removeOperatorParticipations(
            storeCode,
            subject.userId,
          );
          message.success(`Participaciones eliminadas (${deleted})`);
        } catch (e: any) {
          message.error(
            `No se pudieron quitar: ${e?.response?.data?.message ?? e?.message ?? e}`,
          );
        }
      },
    });
  };

  if (!isConversationsConfigured()) return null;

  return (
    <Card
      loading={loading}
      styles={{ body: { padding: 0 } }}
      style={{ marginBottom: 16, borderRadius: 8 }}
    >
      <Flex vertical gap={4} style={{ background: "#fafafa", padding: "12px 16px" }}>
        <Text strong>
          <MessageOutlined /> Conversations
        </Text>
        <Text style={{ fontSize: 12, color: "#8c8c8c" }}>
          Acceso del operador a las conversaciones de cada canal del store.
        </Text>
      </Flex>

      {grants.length === 0 ? (
        <div style={{ padding: 16 }}>
          <Text type="secondary">El store no tiene canales.</Text>
        </div>
      ) : (
        grants.map((g) => (
          <Flex
            key={g.channelId}
            justify="space-between"
            align="center"
            style={{ padding: "12px 16px", borderTop: "1px solid #f0f0f0" }}
          >
            <span>
              <Text strong>{g.name}</Text> <Text type="secondary">{g.identifier}</Text>
            </span>
            {editable ? (
              <Select
                style={{ width: 160 }}
                value={levels[g.channelId] ?? "no_access"}
                options={LEVEL_OPTIONS}
                onChange={(v) =>
                  setLevels((p) => ({ ...p, [g.channelId]: v as ChannelPermissionLevel }))
                }
              />
            ) : (
              <Text type="secondary">
                {LEVEL_OPTIONS.find((o) => o.value === (levels[g.channelId] ?? "no_access"))?.label}
              </Text>
            )}
          </Flex>
        ))
      )}

      {/* El Remove va sólo en la ficha del operador: la del seller no tiene el análogo de
          contactos, y las participaciones son de personas, no de la entidad seller. */}
      {editable && subject.kind === "operator" && grants.length > 0 && (
        <Flex
          justify="space-between"
          align="center"
          style={{ padding: "12px 16px", borderTop: "1px solid #f0f0f0" }}
        >
          <Text>Quitar todas las participaciones existentes en conversaciones</Text>
          <Button danger style={{ width: 180 }} onClick={confirmRemoveParticipations}>
            Remove
          </Button>
        </Flex>
      )}
    </Card>
  );
});

ChannelAccessPermissions.displayName = "ChannelAccessPermissions";
